package com.vizio.redwolf.pay.client

import com.vizio.redwolf.pay.model.CreateSupportCaseRequest
import com.vizio.redwolf.pay.model.SerialNumberRequest
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.CONTENT_TYPE_APP_JSON_UTF8
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.SUBSCRIPTION_API_KEY
import com.vizio.redwolf.utils.applyAppConfig
import com.vizio.redwolf.utils.applyTimeout
import com.vizio.redwolf.utils.model.MobileHeaderInfo
import com.vizio.redwolf.utils.safeHttpCall
import com.vizio.redwolf.utils.setDebugInfo
import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpHeaders
import io.ktor.http.URLProtocol
import io.ktor.http.path
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.encodeToJsonElement

class CustomerSupportHttpClient(private val payEnv: PayEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig) {
    private val httpClient: HttpClient = clientConfig.clientProvider.httpClient

    private val json = Json { encodeDefaults = true }
    private val customerSupportApiKey = payEnv.vizioApiSubscriptionKey

    suspend fun validateSerialNumber(serialNumberRequest: SerialNumberRequest): HttpResult<HttpResponse> {
        val serialNumberRequestInfo = json.encodeToJsonElement(serialNumberRequest)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("${payEnv.payAuthPath}/serial-numbers/validate")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, customerSupportApiKey)
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                }
                setBody(serialNumberRequestInfo)
                applyTimeout(clientConfig.timeout)
                applyAppConfig(appConfig)
            }
        }
    }

    suspend fun getProductAssets(id: String): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("${payEnv.productsPath}/products/$id/assets")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, customerSupportApiKey)
                }
                applyTimeout(clientConfig.timeout)
                applyAppConfig(appConfig)
            }
        }
    }

    suspend fun getSupportHours(): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("${payEnv.marketingApiPath}/marketing/get-support-hours")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, customerSupportApiKey)
                }
                applyTimeout(clientConfig.timeout)
                applyAppConfig(appConfig)
            }
        }
    }

    suspend fun getProductFirmwareVersion(serialNumber: String): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("${payEnv.marketingApiPath}/marketing/get-product-firmware-version")
                    parameters.append(SERIAL_NUMBER, serialNumber)
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, customerSupportApiKey)
                }
                applyTimeout(clientConfig.timeout)
                applyAppConfig(appConfig)
            }
        }
    }

    suspend fun createSupportCase(supportCaseRequest: CreateSupportCaseRequest, mobileHeaderInfo: MobileHeaderInfo?): HttpResult<HttpResponse> {
        val supportCaseRequestInfo = json.encodeToJsonElement(supportCaseRequest)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = payEnv.payHost
                    path("${payEnv.marketingApiPath}/marketing/create-support-case")
                }
                headers {
                    append(SUBSCRIPTION_API_KEY, customerSupportApiKey)
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                }
                applyTimeout(clientConfig.timeout)
                setBody(supportCaseRequestInfo)
                setDebugInfo(mobileHeaderInfo)
            }
        }
    }
    companion object {
        const val SERIAL_NUMBER = "serialNumber"
    }
}